import React, { useEffect, useState, useMemo } from 'react';

import {
	searchCourseByTitle,
	clearCourseSearch as clear,
} from 'actions/search';

import { putData } from 'actions';
import { getCourse } from 'actions/courses';
import { toggleSelectedSection } from 'actions/sections';
import { getSchoolTypes } from 'actions/tenants'
import { getGroupById, updateGroup } from 'actions/groups';


import { useTranslate } from 'lib/translate';
import { useSelector, useDispatch } from 'react-redux';

import Modal from 'containers/Modals/Modal';
import { Spinner } from 'UI';
import { Search } from 'UI/Inputs';
import { Tabs, Tab, Button, Checkbox } from '@haldor/ui';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

const SelectCourses = (props) => {
	const [searching, setSearching] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [selected, setSelected] = useState([]);
	const [addOnGroup, setAddOnGroup] = useState(true);
	const [schoolType, setSchoolType] = useState('All');
	const [year, setYear] = useState('All');
	const [searchValue, setSearchValue] = useState('');
	const [groups, setGroups] = useState([]);
	const sections = useSelector(state => state.sections.selected);
	const group = useSelector(state => state.assignments.section);
	const searchResultsData = useSelector(state => state.Courses.courseSearchResults);
	const courseDetails = useSelector(state => state.Courses.courses);
	const schoolTypes = useSelector(state => state.Tenant.schoolTypes);
	const YEARS = ["All", 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

	const translate = useTranslate();
	const dispatch = useDispatch();

	// Initialize local groups state from Redux sections or group
	useEffect(() => {
		const fetchGroups = async () => {
			if (sections != null && sections.length > 0) {
				// Fetch each section with full details including courses
				const fetchedGroups = await Promise.all(
					sections.map(async (section) => {
						const fullGroup = await dispatch(getGroupById(section.id, ['courses']));
						return fullGroup;
					})
				);
				setGroups(fetchedGroups);
			} else if (group != null) {
				// Handle single group from assignments
				const fullGroup = await dispatch(getGroupById(group.id, ['courses']));
				setGroups([fullGroup]);
			} else {
				setGroups([]);
			}
		};

		fetchGroups();
	}, [sections, group, dispatch]);

	useEffect(() => {
		if (schoolTypes == null || schoolTypes.length == 0) {
			dispatch(getSchoolTypes());
		}
	}, []);

	const sectionCourses = useMemo(() => {
		if (groups == null || groups.length == 0) {
			return [];
		}

		let courses = [];
		groups.forEach((element) => {
			if (element.courses && element.courses.length > 0) {
				element.courses.forEach(course => {
					if (props.skipCourses != null) {
						if (props.skipCourses.indexOf(course.id) > -1) {
							return null;
						}
					}

					courses.push(JSON.parse(JSON.stringify(course)))
				});
			}
		});

		const removeDuplicate = courses.filter((item, index, self) =>
			self.findIndex((t) => t.id === item.id) === index
		);

		return removeDuplicate;
	}, [groups, props.skipCourses]);

	const searchResults = useMemo(() => {
		if (searchResultsData == null || searchResultsData.length == 0) {
			return [];
		}

		return JSON.parse(JSON.stringify(searchResultsData)).filter((course) => {
			if (props.skipCourses == null) {
				return false;
			}

			return props.skipCourses.indexOf(course.id) == -1;
		}).filter((v, i, a) => a.findIndex(v2 => (v2.id === v.id)) === i);
	}, [searchResultsData]);

	const onSubmit = async (event) => {
		event.preventDefault();

		setSubmitting(true);

		try {
			let values = selected;
			const missingDetails = selected.filter(course => course.details.length == 0);

			if (missingDetails.length > 0) {
				await Promise.all(
					missingDetails.map((course) => new Promise(async (resolve) => {
						await dispatch(getCourse(course.id));
						const details = courseDetails.find((c) => c.id == course.id);

						if (details != null) {
							values = values.map((course) =>
								course.id == details.id ? details : course
							);
						}

						resolve();
					}))
				);

				if (addOnGroup && groups.length > 0) {
					// Handle each group one by one and collect updated groups
					const updatedGroups = await Promise.all(
						groups.map(async (localGroup) => {
							dispatch(toggleSelectedSection(localGroup));

							missingDetails.forEach((course) => {
								const courseWithDetails = courseDetails.find((c) => c.id == course.id);
								if (courseWithDetails && localGroup.courses) {
									localGroup.courses.push(courseWithDetails);
								}
							});

							// Update the group and refresh cache with courses expanded
							const updatedGroup = await dispatch(updateGroup(localGroup, ['courses']));

							dispatch(toggleSelectedSection(updatedGroup));
							return updatedGroup;
						})
					);

					// Update local state with the refreshed groups
					setGroups(updatedGroups);
				}
			}

			// Clear selected items after successful submission
			setSelected([]);

			if (props.onSubmit != null) {
				props.onSubmit(values);
			}
		} finally {
			setSubmitting(false);
		}
	}

	const onSearch = () => {
		setSelected([]);

		if (searchValue == '' && schoolType == 'All' && year == 'All') {
			dispatch(clear());
			return;
		}

		setSearching(true);

		dispatch(searchCourseByTitle(
			searchValue == '' ? null : searchValue,
			schoolType == 'All' ? '' : schoolType,
			year == 'All' ? '' : year,
		)).then(() => {
			setSearching(false);
		});
	}

	useEffect(() => {
		onSearch();
	}, [year, schoolType])

	const renderCourse = (course, index) => {
		const value = selected.find((s) => s.id == course.id) != null;
		const schoolType = translate(course.typeOfSchooling);
		const courseTitle = `${course.title} - ${course.year != null ? course.year : ''} ${course.courseCode} (${schoolType})`;

		return (
			<div className="df aic" style={{ marginBottom: '.55rem' }} key={course.id}>
				<Checkbox
					label={courseTitle}
					onChange={() => toggleCourse(course)}
					value={value}
				/>
			</div>
		);
	}

	const toggleCourse = (course) => {
		const found = selected.find((s) => s.id == course.id);
		if (found != null) {
			setSelected(selected.filter((s) => s.id != course.id));
		} else {
			setSelected([...selected, course]);
		}
	}

	const toggleAll = (checked) => {
		if (checked) {
			let courses = [];
			groups.forEach(element => {
				if (element.courses && element.courses.length > 0) {
					element.courses.forEach(course =>
						courses.push(course)
					);
				}
			});

			setSelected([...courses]);
			return;
		};

		setSelected([]);
	}

	const selectedSchoolType = schoolTypes?.find((type) =>
		type.typeId == schoolType
	);

	return <Modal isOpen={true} type="small news" onClose={props.onClose} overridePrompt>
		<div className="form" style={{ marginBottom: '2.5rem' }}>
			<div className="form-row">
				<div className="title">
					{translate('Courses')}
				</div>

				<Tabs onChange={() => setSelected([])}>
					<Tab title={translate('Group courses')}>
						{sectionCourses.length > 0 ?
							<>
								<Checkbox
									label={translate('All')}
									onChange={(value) => toggleAll(value)}
								/>

								<div style={{ marginLeft: '2.25rem', marginTop: '.55rem' }}>
									{sectionCourses.map((course, index) => {
										return <div key={index}>
											{renderCourse(course, index)}
										</div>
									})}
								</div>
							</>
							: null}

						<div className="df aic jcb" style={{ marginTop: '1.55rem' }}>
							<Button onClick={onSubmit} disabled={submitting || selected.length == 0}>
								{submitting ?
									<Spinner />
									: null}

								{translate('Add')}
							</Button>
						</div>
					</Tab>

					<Tab title={translate('Search courses')}>
						<Search
							onSubmit={onSearch}
							loading={searching}
							onChange={(value) => setSearchValue(value)}
							allowEmpty
						/>

						<div className="df" style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
							<div style={{ width: '47%', padding: '0px !important', marginRight: '1rem' }}>
								<label style={{ margin: 0 }}>
									{translate('Filter')}
								</label>

								<div className='select' style={{ marginTop: '.5rem' }}>
									<FormControl fullWidth size="small">
										<Select sx={{ backgroundColor: '#FFFFFF' }} name='schoolType'
											value={schoolType}
											onChange={(e) => setSchoolType(e.target.value)}
											displayEmpty
											MenuProps={{ disablePortal: true }}
										>
											<MenuItem value="All" key="school-type-all">
												{translate('All')}
											</MenuItem>

											{schoolTypes?.sort((a, b) =>
												translate(a.typeId).localeCompare(translate(b.typeId))
											).map((type) => (
												<MenuItem key={type.typeId} value={type.typeId}>
													{translate(type.typeId)}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</div>
							</div>

							{selectedSchoolType != null && selectedSchoolType.maxYear != null && selectedSchoolType.minYear != null ?
								<div style={{ padding: 0, width: 'auto', flex: 1, marginLeft: '2rem' }}>
									<label style={{ margin: 0 }}>
										{translate('course-year')}
									</label>

									<div className='select' style={{ marginTop: '8px' }}>
										<FormControl fullWidth size="small">
											<Select
												name='year'
												value={year}
												onChange={(e) => setYear(e.target.value)}
												displayEmpty
												MenuProps={{ disablePortal: true }}
											>
												{YEARS
													.filter((optionYear) => {
														if (optionYear == 'All') {
															return true;
														}

														return optionYear >= selectedSchoolType?.minYear && optionYear <= selectedSchoolType?.maxYear;
													})
													.map((optionYear) => (
														<MenuItem key={optionYear} value={optionYear}>
															{translate(optionYear)}
														</MenuItem>
													))}
											</Select>
										</FormControl>
									</div>
								</div>
								: null}
						</div>

						<div style={{ marginTop: '1rem' }}>
							{searchResults.map(renderCourse)}
						</div>

						<div className="df aic jcb" style={{ marginTop: '1.55rem' }}>
							<Button onClick={onSubmit} disabled={submitting || selected.length == 0}>
								{submitting ?
									<Spinner />
									: null}

								{translate('Add')}
							</Button>

							<Checkbox
								value={addOnGroup}
								onChange={setAddOnGroup}
								label={translate('Add course to group')}
							/>
						</div>
					</Tab>
				</Tabs>

			</div>
		</div>
	</Modal >
}

export default SelectCourses;